import { DateTime } from "luxon";
import { SubjectSummaryCard } from "../../components/SubjectSummaryCard";
import { buildName, capitalise } from "../../utils/string";

/**
 * Who is being monitored, and which record this is, as a header band.
 *
 * Thin wrapper over SubjectSummaryCard supplying monitoring's own fields.
 * Replaces the left-hand Details rail on a LexisNexis2 record. Horizontal
 * rather than a rail because the match tables need the width: three columns
 * left the profile comparison and the relationships table noticeably tighter
 * than they are in the mandate check's slider.
 *
 * Deliberately does not carry a delete date or a data-retention statement. The
 * retention policy applies to screening records, not to monitoring records, and
 * the legacy page shows both on entity records only, which looks like an
 * oversight rather than a decision.
 */

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : "-";

interface Props {
  monitoring: any;
  isEntity: boolean;
  countryLabel?: string;
  countryFlag?: string;
  entityTypeName?: string;
}

export const MonitoringRecordSummary = ({
  monitoring,
  isEntity,
  countryLabel,
  countryFlag,
  entityTypeName,
}: Props) => {
  const name = isEntity
    ? monitoring?.company_name
    : buildName([
        monitoring?.first_name,
        monitoring?.middle_name,
        monitoring?.last_name,
      ]);

  // absences are stated rather than omitted, so a missing date of birth reads as
  // a fact about the record and not as something that failed to load
  const identityFacts = isEntity
    ? [
        countryLabel ? capitalise(countryLabel) : null,
        monitoring?.date_of_incorporation
          ? `Incorporated ${formatDate(monitoring.date_of_incorporation)}`
          : null,
        monitoring?.entity_type ? entityTypeName : null,
        monitoring?.registration_number
          ? `No. ${monitoring.registration_number}`
          : null,
      ]
    : [
        monitoring?.date_of_birth
          ? `Born ${formatDate(monitoring.date_of_birth)}`
          : "No date of birth",
        monitoring?.gender ? capitalise(monitoring.gender) : null,
      ];

  return (
    <SubjectSummaryCard
      name={name}
      chipLabel={isEntity ? "Entity" : "Individual"}
      identityFacts={identityFacts}
      countryFlag={isEntity ? countryFlag : undefined}
      fields={[
        { label: "Reference", value: monitoring?.reference },
        { label: "Your reference", value: monitoring?.external_reference },
        { label: "Started", value: formatDate(monitoring?.created_on) },
        { label: "Last activity", value: formatDate(monitoring?.last_activity) },
      ]}
    />
  );
};
